import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Interface for the hierarchy.json structure
 */
export interface HierarchyQuestion {
  question_number: number;
  global_number: number;
  page: string;
}

export interface HierarchyItem {
  name: string;
  marker?: string;
  title?: string;
  numeral?: string;
  questions: HierarchyQuestion[];
  subtopics?: HierarchySubtopic[];
  direct_questions?: HierarchyQuestion[];
  first_seen_page?: string;
}

export interface HierarchySubtopic {
  name: string;
  marker?: string;
  questions: HierarchyQuestion[];
  first_seen_page?: string;
}

export interface HierarchyTopic extends HierarchyItem {
  subtopics: HierarchySubtopic[];
}

export interface HierarchySection extends HierarchyItem {
  title: string;
  numeral: string;
  topics: HierarchyTopic[];
  orphan_subtopics?: HierarchySubtopic[];
}

export interface HierarchyJSON {
  subject: string;
  subject_topic_id: string;
  language: string;
  sections: HierarchySection[];
  orphan_topics: HierarchyTopic[];
  total_questions: number;
  total_topics: number;
  total_subtopics: number;
  total_sections: number;
  last_updated_page?: string;
  processing_status?: string;
}

export interface SubjectHierarchy {
  subjectName: string;
  subjectId: string;
  hierarchies: HierarchyJSON[];
  language: string;
}

@Injectable({
  providedIn: 'root'
})
export class HierarchyJsonLoaderService {
  private apiUrl = `${environment.apiBaseUrl}/api`;
  private cachedHierarchies: Map<string, HierarchyJSON[]> = new Map();

  constructor(private http: HttpClient) {}

  /**
   * Get all available hierarchy JSON files for all subjects
   * Returns an observable that emits all subject hierarchies
   */
  getAllSubjectHierarchies(): Observable<SubjectHierarchy[]> {
    console.log('📂 Loading all subject hierarchies from backend...');
    
    // Call backend endpoint to get list of all available hierarchy.json files
    return this.http.get<SubjectHierarchy[]>(`${this.apiUrl}/hierarchies/all`).pipe(
      tap(hierarchies => {
        console.log(`✅ Loaded ${hierarchies.length} subject hierarchies`);
        // Cache them
        hierarchies.forEach(sh => {
          if (!this.cachedHierarchies.has(sh.subjectName)) {
            this.cachedHierarchies.set(sh.subjectName, sh.hierarchies);
          }
        });
      }),
      catchError(error => {
        console.error('❌ Error loading Subject Hierarchies:', error);
        return of([]); // Return empty array on error
      })
    );
  }

  /**
   * Get hierarchies for a specific subject
   * Returns all hierarchy.json files for batches of that subject
   */
  getSubjectHierarchies(subjectName: string): Observable<HierarchyJSON[]> {
    console.log(`📂 Loading hierarchies for subject: ${subjectName}`);
    
    // Check cache first
    if (this.cachedHierarchies.has(subjectName)) {
      return of(this.cachedHierarchies.get(subjectName) || []);
    }

    return this.http.get<HierarchyJSON[]>(`${this.apiUrl}/hierarchies/${encodeURIComponent(subjectName)}`).pipe(
      tap(hierarchies => {
        console.log(`✅ Loaded ${hierarchies.length} hierarchies for ${subjectName}`);
        this.cachedHierarchies.set(subjectName, hierarchies);
      }),
      catchError(error => {
        console.error(`❌ Error loading hierarchies for ${subjectName}:`, error);
        return of([]); // Return empty array on error
      })
    );
  }

  /**
   * Get a single hierarchy JSON file by subject and batch index
   */
  getHierarchy(subjectName: string, batchIndex: number = 0): Observable<HierarchyJSON> {
    console.log(`📂 Loading hierarchy for subject: ${subjectName}, batch: ${batchIndex}`);
    
    return this.http.get<HierarchyJSON>(
      `${this.apiUrl}/hierarchies/${encodeURIComponent(subjectName)}/${batchIndex}`
    ).pipe(
      catchError(error => {
        console.error(`❌ Error loading hierarchy for ${subjectName}:`, error);
        return of({
          subject: subjectName,
          subject_topic_id: '',
          language: 'english',
          sections: [],
          orphan_topics: [],
          total_questions: 0,
          total_topics: 0,
          total_subtopics: 0,
          total_sections: 0
        });
      })
    );
  }

  /**
   * Get list of available subjects from all hierarchy.json files
   * This should be used instead of the API-based subject list
   */
  getAvailableSubjectsFromHierarchies(): Observable<string[]> {
    console.log('📚 Getting available subjects from hierarchies...');
    
    return this.http.get<string[]>(`${this.apiUrl}/hierarchies/subjects`).pipe(
      tap(subjects => {
        console.log(`✅ Found ${subjects.length} subjects with hierarchy data`);
      }),
      catchError(error => {
        console.error('❌ Error loading subjects from hierarchies:', error);
        return of([]); // Return empty array on error
      })
    );
  }

  /**
   * Convert a hierarchy.json structure to the tree service format
   * This is used to populate the application's data structures
   */
  convertHierarchyToTreeFormat(
    hierarchy: HierarchyJSON,
    imageBaseUrl: string = ''
  ): {
    sections: Map<string, any>;
    orphanTopics: any[];
  } {
    const sections = new Map<string, any>();
    const orphanTopics: any[] = [];

    // Process sections
    hierarchy.sections.forEach(section => {
      const sectionData = {
        id: `section_${section.name}`,
        hindiName: section.title,
        topics: new Map<string, any>()
      };

      // Process topics within section
      section.topics.forEach(topic => {
        const topicData = {
          id: `topic_${topic.name}`,
          hindiName: topic.name,
          subtopics: new Map<string, any>(),
          questions: this.convertQuestions(topic.questions, imageBaseUrl)
        };

        // Process subtopics
        topic.subtopics.forEach(subtopic => {
          topicData.subtopics.set(subtopic.name, {
            id: `subtopic_${subtopic.name}`,
            hindiName: subtopic.name,
            questions: this.convertQuestions(subtopic.questions, imageBaseUrl)
          });
        });

        sectionData.topics.set(topic.name, topicData);
      });

      sections.set(section.name, sectionData);
    });

    // Process orphan topics (topics not under any section)
    hierarchy.orphan_topics.forEach(topic => {
      orphanTopics.push({
        id: `orphan_topic_${topic.name}`,
        name: topic.name,
        marker: topic.marker,
        subtopics: (topic.subtopics || []).map(subtopic => ({
          id: `orphan_subtopic_${subtopic.name}`,
          name: subtopic.name,
          questions: this.convertQuestions(subtopic.questions, imageBaseUrl)
        })),
        questions: this.convertQuestions(topic.questions, imageBaseUrl)
      });
    });

    return { sections, orphanTopics };
  }

  /**
   * Convert question data with proper image URLs
   */
  private convertQuestions(questions: HierarchyQuestion[], imageBaseUrl: string): any[] {
    return questions.map(q => ({
      id: `q_${q.question_number}_${q.global_number}`,
      question_number: q.question_number,
      global_number: q.global_number,
      page: q.page,
      imageUrl: this.buildImageUrl(q.page, 'question', imageBaseUrl)
    }));
  }

  /**
   * Build image URL from page name and type
   */
  private buildImageUrl(pageName: string, type: 'question' | 'answer' | 'explanation', baseUrl: string): string {
    if (!pageName) return '';

    // Clean up page name and build image path
    const cleanPageName = pageName.replace(/\s+/g, '_');
    
    if (baseUrl) {
      return `${baseUrl}/${cleanPageName}_${type}.png`;
    }

    // Default to API image serving if no base URL provided
    return `${environment.apiBaseUrl}/api/gcs-files/${cleanPageName}_${type}.png`;
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cachedHierarchies.clear();
    console.log('🧹 Hierarchy cache cleared');
  }
}
